import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BRAND, NAV } from "../lib/constants";

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNavClick = (e, href) => {
        e.preventDefault();
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/" + href);
            return;
        }
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-ls-bg/80 backdrop-blur-xl border-b border-ls-ink/10"
                    : "bg-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
                <Link
                    to="/"
                    className="flex items-center gap-3 group"
                    data-testid="logo-link"
                >
                    <div className="w-10 h-10 rounded-sm overflow-hidden border border-ls-ink/10 bg-ls-surface flex-shrink-0 transition-transform duration-500 group-hover:rotate-[-4deg]">
                        <img
                            src={BRAND.logo}
                            alt="LS Design & Consulting"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="leading-none">
                        <div className="font-serif text-lg text-ls-ink tracking-tight">
                            LS
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-ls-inkSoft font-sans mt-0.5">
                            Design & Consulting
                        </div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV.map((n) => (
                        <a
                            key={n.label}
                            href={n.href}
                            onClick={(e) => handleNavClick(e, n.href)}
                            className="text-sm font-sans text-ls-inkSoft hover:text-ls-ink transition-colors ls-underline"
                            data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                        >
                            {n.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <a
                        href={BRAND.instagram}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="p-2 text-ls-ink hover:text-ls-accent transition-colors"
                        aria-label="Instagram"
                        data-testid="header-instagram"
                    >
                        <Instagram size={18} />
                    </a>
                    <a
                        href="#contact"
                        onClick={(e) => handleNavClick(e, "#contact")}
                        className="inline-flex items-center gap-2 bg-ls-ink text-ls-bg px-6 py-3 text-xs uppercase tracking-[0.22em] font-sans hover:bg-ls-accent transition-colors rounded-full group"
                        data-testid="header-cta"
                    >
                        Beratung anfragen
                        <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                            →
                        </span>
                    </a>
                </div>

                <button
                    className="md:hidden text-ls-ink p-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Menü öffnen"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="md:hidden bg-ls-bg border-t border-ls-ink/10 overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-8 flex flex-col gap-6">
                            {NAV.map((n) => (
                                <a
                                    key={n.label}
                                    href={n.href}
                                    onClick={(e) => handleNavClick(e, n.href)}
                                    className="font-serif text-2xl text-ls-ink"
                                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                                >
                                    {n.label}
                                </a>
                            ))}
                            <a
                                href="#contact"
                                onClick={(e) => handleNavClick(e, "#contact")}
                                className="mt-4 inline-flex items-center justify-center bg-ls-ink text-ls-bg px-6 py-4 text-xs uppercase tracking-[0.22em] font-sans rounded-full"
                                data-testid="mobile-cta"
                            >
                                Beratung anfragen →
                            </a>
                            <a
                                href={BRAND.instagram}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="mt-2 inline-flex items-center gap-3 text-ls-ink font-sans text-sm"
                                data-testid="mobile-instagram"
                            >
                                <Instagram size={18} />
                                {BRAND.instagramHandle}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;
